/**
 * useLogoUpload — brand-logo file upload handlers for the Deploy wizard.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * REQUIRED <input type="file"> CONFIGURATION (gallery-only, no camera)
 * ──────────────────────────────────────────────────────────────────────────
 *
 * The `onSelectFile` handler returned by this hook is meant to be wired as
 * the `onChange` of a `<input type="file">` defined in the templatization
 * config. To guarantee the OS opens **only the gallery / Photo Picker**
 * (and never offers the camera) on Android and iOS, the input MUST be
 * configured as follows:
 *
 *   <input
 *     type="file"
 *     accept="image/jpeg,image/png,image/svg+xml"
 *     onChange={actions.onSelectFile}
 *   />
 *
 * Rules — both must hold:
 *
 *   1. `accept` must be an EXPLICIT MIME list, NOT `image/*`.
 *        ✓ accept="image/jpeg,image/png,image/svg+xml"
 *        ✗ accept="image/*"          (Android may surface the Camera app)
 *        ✗ accept=".png,.jpg,.svg"   (extension-only is less reliable)
 *
 *      Android: the system intent chooser routes the request to apps that
 *      declare matching MIME filters. With specific image MIMEs (no
 *      wildcard), the chooser opens the system Photo Picker / gallery
 *      apps; Camera does not declare those MIMEs and is excluded.
 *
 *      iOS (Safari / WKWebView): the action sheet derives its options
 *      from `accept`. With image MIMEs it offers "Photo Library" and
 *      "Choose Files"; "Take Photo or Video" is not listed.
 *
 *   2. The `capture` attribute MUST be ABSENT.
 *        ✓ <input type="file" accept="..." />
 *        ✗ <input type="file" accept="..." capture />
 *        ✗ <input type="file" accept="..." capture="environment" />
 *        ✗ <input type="file" accept="..." capture="user" />
 *
 *      The mere presence of `capture` (any value, including empty) is a
 *      hint to the user agent to prefer the camera. On Android this
 *      typically launches the Camera app directly; on iOS it surfaces
 *      "Take Photo or Video" as the default. Do not set it.
 *
 * Optional attributes:
 *
 *   - `multiple` is not required; this handler only consumes the first
 *     valid file it finds in the list and stops.
 *
 * If either rule above is violated, the camera may appear as an option —
 * the hook cannot enforce this from the JS side because it only receives
 * the `change` event after the OS picker has already been chosen.
 * ──────────────────────────────────────────────────────────────────────────
 */

import React from 'react';
import { isNil } from 'lodash-es';

import { hasText } from '@/utils/strings';

import { DeployState } from '../types';

/**
 * Maximum upload size in bytes (12 MiB).
 */
const MAX_SIZE = 12 * 1024 * 1024;

/**
 * Allowed image type fragments. Matched against `File.type` via `String.includes`,
 * so each entry should be a substring that appears in the corresponding MIME type
 * (e.g. `svg` matches `image/svg+xml`).
 */
const ALLOWED_TYPES = ['jpeg', 'jpg', 'png', 'svg'];

export interface UseLogoUploadOptions {
  /**
   * State setter from the parent `DeployState`. The hook only ever mutates
   * `selectedLogo` and `uploadedFile` through it.
   */
  setState: React.Dispatch<React.SetStateAction<DeployState>>;
}

export interface UseLogoUploadResult {
  /**
   * `onChange` handler for a `<input type="file">`. To guarantee the OS opens
   * the gallery / Photo Picker (and never the camera) on Android and iOS, the
   * input must be configured with:
   *   - `accept="image/jpeg,image/png,image/svg+xml"` (explicit MIMEs, not `image/*`)
   *   - **no** `capture` attribute (its presence forces the camera on mobile)
   */
  onSelectFile: React.ChangeEventHandler<HTMLInputElement>;
  /** Clears any selected logo and the uploaded file reference. */
  onResetSelectedLogo: () => void;
}

/**
 * Encapsulates all logic and handlers related to selecting / uploading a
 * brand logo image file from the user's device.
 *
 * The hook is deliberately decoupled from the rest of the wizard: it does not
 * own the wider `DeployState`, it only patches the two fields it cares about
 * (`selectedLogo`, `uploadedFile`) through the provided setter.
 */
export function useLogoUpload({ setState }: UseLogoUploadOptions): UseLogoUploadResult {
  const onSelectFile = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const files = event?.target?.files;

      if (isNil(files)) {
        return;
      }

      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);

        if (isNil(file)) {
          return;
        }

        if (file.size >= MAX_SIZE) {
          continue;
        }

        if (!ALLOWED_TYPES.some((type) => file.type.includes(type))) {
          continue;
        }

        const reader = new FileReader();

        reader.onload = (progressEvent) => {
          setState((prevState) => ({
            ...prevState,
            selectedLogo: hasText(progressEvent?.target?.result) ? progressEvent.target.result : '',
            uploadedFile: file,
          }));
        };

        reader.readAsDataURL(file);

        return;
      }
    },
    [setState],
  );

  const onResetSelectedLogo = React.useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      selectedLogo: null,
      uploadedFile: null,
    }));
  }, [setState]);

  return React.useMemo(() => ({ onSelectFile, onResetSelectedLogo }), [onSelectFile, onResetSelectedLogo]);
}
